/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {CommonModule} from '@angular/common';
import {Component, signal} from '@angular/core';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef} from 'ag-grid-community';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';

@Component({
    selector: 'app-component-id-picker-dialog',
    standalone: true,
    imports: [CommonModule, AgGridAngular],
    template: `
        <ag-grid-angular
            class="ag-theme-balham componentCatalog"
            style="width: 100%; height: 55vh;"
            [columnDefs]="columnDefs"
            [rowData]="rows"
            [defaultColDef]="defaultColDef"
            [rowSelection]="'single'"
            (selectionChanged)="onSelectionChanged($event)">
        </ag-grid-angular>
        <div class="picker-actions">
            <button type="button" class="p-button p-component gm-btn-primary"
                    [disabled]="!selectedRow()" (click)="confirmSelection()">Select</button>
            <button type="button" class="p-button p-component p-button-secondary"
                    (click)="clearSelection()">Clear</button>
            <button type="button" class="p-button p-component p-button-text"
                    (click)="dialogRef.close()">Cancel</button>
        </div>
    `,
    styles: [`
        .picker-actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-top: 1rem;
        }
    `]
})
export class ComponentIdPickerDialogComponent {
    public readonly rows: any[];
    public readonly columnDefs: ColDef[];
    public readonly defaultColDef: ColDef = {sortable: true, filter: true, resizable: true, flex: 1};
    public readonly selectedRow = signal<any | null>(null);

    constructor(public config: DynamicDialogConfig, public dialogRef: DynamicDialogRef) {
        this.rows = Array.isArray(config.data?.rows) ? config.data.rows : [];
        const fields = Array.isArray(config.data?.fields) ? config.data.fields : [];
        this.columnDefs = fields.map((field: string) => ({
            field,
            headerName: ComponentIdPickerDialogComponent.humanizeFieldName(field)
        }));
    }

    public onSelectionChanged(event: any): void {
        this.selectedRow.set(event?.api?.getSelectedRows?.()[0] ?? null);
    }

    public confirmSelection(): void {
        const selected = this.selectedRow();
        if (selected) {
            this.dialogRef.close(selected);
        }
    }

    public clearSelection(): void {
        this.dialogRef.close(null);
    }

    private static humanizeFieldName(fieldName: string): string {
        const normalized = fieldName.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
        return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
    }
}
